import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Order, SortDirection } from '@klareospace/pharos-lib-types';
import { ORDER_STATUS } from '@klareospace/pharos-lib-types';
import type { DataTableColumn, DataTableColumnReorder, SimplePagination } from '@klareospace/pharos-lib-web-utils';
import { DataTable, useDataTablePagination, useDataTableSorting } from '@klareospace/pharos-lib-web-utils';

import type { OrderSortableColumn } from '../types/order.types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrdersSorting {
  onSort: (field: OrderSortableColumn, direction: SortDirection | null) => void;
  sortBy: OrderSortableColumn | undefined;
  sortDirection: SortDirection | undefined;
}

export interface OrdersTableProps {
  columnReorder: DataTableColumnReorder;
  columns: DataTableColumn<Order>[];
  isLoading: boolean;
  orders: Order[];
  pagination: SimplePagination;
  sorting: OrdersSorting;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function OrdersTable({
  columnReorder,
  columns,
  isLoading,
  orders,
  pagination,
  sorting,
}: OrdersTableProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('orders');

  const paginationLabels = useMemo(
    () => ({
      nextPage: tCommon('pagination.nextPage'),
      pageLabel: (current: number, total: number): string => tCommon('pagination.pageLabel', { current, total }),
      pageSizeLabel: tCommon('pagination.rowsPerPage'),
      previousPage: tCommon('pagination.previousPage'),
      showingText: (start: number, end: number, total: number): string =>
        t('table.showing', { from: start, to: end, total }),
    }),
    [t, tCommon],
  );

  const tablePagination = useDataTablePagination({ labels: paginationLabels, pagination });

  const tableSorting = useDataTableSorting<OrderSortableColumn>({
    onSort: sorting.onSort,
    sortBy: sorting.sortBy,
    sortDirection: sorting.sortDirection,
  });

  const getRowClassName = React.useCallback((order: Order): string | undefined => {
    const isAlert = order.status === ORDER_STATUS.ANOMALY || order.status === ORDER_STATUS.CARRIER_REJECTED;
    return isAlert ? 'shadow-[inset_6px_0_0_0_var(--color-destructive)] hover:bg-destructive/5' : undefined;
  }, []);

  return (
    <DataTable<Order>
      ariaLabel={t('table.ariaLabel')}
      columnReorder={columnReorder}
      columns={columns}
      emptyMessage={t('table.empty')}
      isLoading={isLoading}
      pagination={tablePagination}
      rowClassName={getRowClassName}
      rowKey={(order): string => order.id}
      rows={orders}
      sorting={tableSorting}
      tableClassName="table-auto"
    />
  );
}
